/**
 * Shared docs renderer: category sidebar + MDX content area.
 *
 * Rendered by both the public /docs page (router-driven) and the in-app
 * DocsPanel modal (state-driven). The parent owns the active section and
 * navigation; this component owns layout, styling, and MDX rendering.
 */

import { Suspense, useState } from 'react';
import { MDXProvider } from '@mdx-js/react';
import { CATEGORIES, SECTIONS, visibleCategories, sectionLabel } from './registry';
import mdxComponents from './mdxComponents';

function PlaceholderContent({ title }) {
  return (
    <div>
      <h1 style={{ fontSize: '24px', fontWeight: 600, color: '#e0e0e0', marginTop: 0, marginBottom: '16px' }}>
        {title}
      </h1>
      <p style={{ color: '#6b7280', fontStyle: 'italic', lineHeight: 1.7 }}>
        Documentation for this section is coming soon.
      </p>
    </div>
  );
}

function LoadingContent() {
  return (
    <div style={{ color: '#4a5568', fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>
      Loading…
    </div>
  );
}

export default function DocsContent({ activeSection, onSelectSection, role = null, maxHeight = null }) {
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const expanded = {};
    CATEGORIES.forEach(cat => { expanded[cat.title] = true; });
    return expanded;
  });

  const toggleCategory = (title) =>
    setExpandedCategories(prev => ({ ...prev, [title]: !prev[title] }));

  const categories = visibleCategories(role);
  const SectionComponent = SECTIONS[activeSection];

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
      {/* Sidebar */}
      <div style={{
        width: '240px',
        flexShrink: 0,
        borderRight: '1px solid #2a3344',
        overflowY: 'auto',
        padding: '12px 0',
      }}>
        {categories.map((cat) => (
          <div key={cat.title} style={{ marginBottom: '4px' }}>
            <button
              onClick={() => toggleCategory(cat.title)}
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                padding: '6px 12px',
                border: 'none',
                background: 'transparent',
                color: '#8a9ab0',
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                cursor: 'pointer',
                textAlign: 'left',
                gap: '6px',
              }}
            >
              <span style={{
                fontSize: '9px',
                transition: 'transform 0.15s',
                transform: expandedCategories[cat.title] ? 'rotate(90deg)' : 'rotate(0deg)',
              }}>
                ▶
              </span>
              {cat.title}
            </button>

            {expandedCategories[cat.title] && cat.items.map((item) => {
              const isActive = item.id === activeSection;
              const hasContent = !!SECTIONS[item.id];
              return (
                <button
                  key={item.id}
                  onClick={() => onSelectSection(item.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '6px 12px 6px 28px',
                    border: 'none',
                    borderLeft: isActive ? '3px solid #4a9eff' : '3px solid transparent',
                    background: isActive ? 'rgba(74, 158, 255, 0.08)' : 'transparent',
                    color: isActive ? '#4a9eff' : (hasContent ? '#c0c8d8' : '#4a5568'),
                    fontSize: '13px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(74, 158, 255, 0.04)';
                      e.currentTarget.style.color = hasContent ? '#e0e0e0' : '#6b7280';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = hasContent ? '#c0c8d8' : '#4a5568';
                    }
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Content area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '28px 48px 48px',
        fontSize: '16px',
        ...(maxHeight ? { maxHeight } : {}),
      }}>
        <MDXProvider components={mdxComponents}>
          {SectionComponent ? (
            <Suspense fallback={<LoadingContent />}>
              <SectionComponent />
            </Suspense>
          ) : (
            <PlaceholderContent title={sectionLabel(activeSection)} />
          )}
        </MDXProvider>
      </div>
    </div>
  );
}
